import { THEME_ENGINE_VERSION, type ThemePackage } from "./format";

export const REFERENCE_THEME_ID = "nexus-aurora";

/**
 * "Nexus Aurora" — the first reference theme shipped through the Secure Theme
 * Engine. It demonstrates the full contract: bilingual settings, design
 * tokens (with `$setting:` references so merchants can recolor/retype the
 * whole store), a section/slot layout tree for every storefront region built
 * only from vetted platform components, one sandboxed template, and scoped
 * author CSS.
 *
 * Concept: a calm "aurora" gradient identity — soft indigo→teal, generous
 * radii, airy spacing — that reads cleanly in both RTL Arabic and LTR English.
 */
export const REFERENCE_THEME: ThemePackage = {
  manifest: {
    id: REFERENCE_THEME_ID,
    name: { ar: "نكسس أورورا", en: "Nexus Aurora" },
    description: {
      ar: "ثيم افتراضي أنيق بألوان متدرجة هادئة ودعم كامل للعربية والإنجليزية.",
      en: "An elegant default theme with calm gradient colors and full Arabic/English support.",
    },
    version: "1.0.0",
    author: "Nexus Cart",
    engineVersion: THEME_ENGINE_VERSION,
    settings: [
      {
        key: "primaryColor",
        type: "color",
        label: { ar: "اللون الأساسي", en: "Primary color" },
        group: { ar: "الألوان", en: "Colors" },
        default: "#4f46e5",
      },
      {
        key: "accentColor",
        type: "color",
        label: { ar: "لون التمييز", en: "Accent color" },
        group: { ar: "الألوان", en: "Colors" },
        default: "#14b8a6",
      },
      {
        key: "backgroundColor",
        type: "color",
        label: { ar: "لون الخلفية", en: "Background color" },
        group: { ar: "الألوان", en: "Colors" },
        default: "#ffffff",
      },
      {
        key: "textColor",
        type: "color",
        label: { ar: "لون النص", en: "Text color" },
        group: { ar: "الألوان", en: "Colors" },
        default: "#0f172a",
      },
      {
        key: "fontAr",
        type: "font",
        label: { ar: "خط العربية", en: "Arabic font" },
        group: { ar: "الخطوط", en: "Typography" },
        default: "Tajawal, system-ui, sans-serif",
      },
      {
        key: "fontEn",
        type: "font",
        label: { ar: "الخط الإنجليزي", en: "English font" },
        group: { ar: "الخطوط", en: "Typography" },
        default: "Inter, system-ui, sans-serif",
      },
      {
        key: "cornerStyle",
        type: "select",
        label: { ar: "نمط الحواف", en: "Corner style" },
        group: { ar: "الشكل", en: "Shape" },
        default: "16px",
        options: [
          { value: "0px", label: { ar: "حادة", en: "Sharp" } },
          { value: "8px", label: { ar: "خفيفة", en: "Soft" } },
          { value: "16px", label: { ar: "دائرية", en: "Rounded" } },
          { value: "999px", label: { ar: "كبسولة", en: "Pill" } },
        ],
      },
      {
        key: "showPromoStrip",
        type: "boolean",
        label: { ar: "إظهار شريط العروض", en: "Show promo strip" },
        group: { ar: "العناصر", en: "Elements" },
        default: true,
      },
      {
        key: "showNewsletter",
        type: "boolean",
        label: { ar: "إظهار النشرة البريدية", en: "Show newsletter" },
        group: { ar: "العناصر", en: "Elements" },
        default: true,
      },
      {
        key: "announcementAr",
        type: "text",
        label: { ar: "نص الإعلان (عربي)", en: "Announcement text (Arabic)" },
        group: { ar: "المحتوى", en: "Content" },
        default: "شحن مجاني للطلبات فوق ٢٠٠ ريال",
      },
      {
        key: "announcementEn",
        type: "text",
        label: { ar: "نص الإعلان (إنجليزي)", en: "Announcement text (English)" },
        group: { ar: "المحتوى", en: "Content" },
        default: "Free shipping on orders over 200 SAR",
      },
    ],
  },

  tokens: {
    colors: {
      primary: "$setting:primaryColor",
      accent: "$setting:accentColor",
      background: "$setting:backgroundColor",
      text: "$setting:textColor",
      "header-bg": "#ffffff",
      "footer-bg": "#0f172a",
      "footer-text": "#e2e8f0",
    },
    fonts: {
      ar: "$setting:fontAr",
      en: "$setting:fontEn",
      heading: "$setting:fontEn",
    },
    radii: {
      base: "$setting:cornerStyle",
      card: "$setting:cornerStyle",
      button: "$setting:cornerStyle",
      banner: "$setting:cornerStyle",
    },
    spacing: {
      section: "3rem",
      gutter: "1.25rem",
    },
  },

  layout: {
    header: [
      {
        id: "promo",
        component: "header.promostrip",
        visible: "$setting:showPromoStrip",
        settings: {
          textAr: "$setting:announcementAr",
          textEn: "$setting:announcementEn",
        },
      },
      {
        id: "main-bar",
        component: "header.bar",
        slots: {
          start: [{ id: "logo", component: "header.logo" }],
          center: [{ id: "nav", component: "header.nav" }],
          end: [
            { id: "search", component: "header.search" },
            { id: "actions", component: "header.actions" },
          ],
        },
      },
    ],
    footer: [
      {
        id: "footer-cols",
        component: "footer.columns",
      },
      {
        id: "footer-newsletter",
        component: "footer.newsletter",
        visible: "$setting:showNewsletter",
      },
      { id: "footer-social", component: "footer.social" },
      { id: "footer-payment", component: "footer.payment" },
      { id: "footer-copy", component: "footer.copyright" },
    ],
    home: [
      // A sandboxed welcome strip rendered ABOVE the routed home page, then the
      // single content anchor (`home.sections`) that delegates to the routed
      // page. Exactly one passthrough node per page region so the routed page is
      // never rendered more than once.
      {
        id: "intro",
        component: "template.slot",
        template: "homeIntro",
      },
      { id: "sections", component: "home.sections" },
    ],
    product: [
      { id: "product", component: "product.detail" },
    ],
    listing: [
      { id: "listing", component: "listing.grid" },
    ],
    cart: [
      { id: "cart", component: "cart.view" },
    ],
    customPage: [
      { id: "page", component: "page.content" },
    ],
  },

  templates: {
    // Markup-only welcome strip. Interpolations are HTML-escaped by the
    // engine; this whole block is sanitized server-side and again by
    // DOMPurify on the client before insertion.
    homeIntro: `<div class="nx-aurora-intro">
  {{# if store.name }}<h2 class="nx-aurora-intro__title">{{ store.name }}</h2>{{/ if }}
  {{# if store.tagline }}<p class="nx-aurora-intro__text">{{ store.tagline }}</p>{{/ if }}
</div>`,
  },

  styles: `
.nx-aurora-intro {
  text-align: center;
  padding: var(--nx-space-section, 3rem) var(--nx-space-gutter, 1.25rem);
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--nx-color-primary, #4f46e5) 8%, transparent),
    color-mix(in srgb, var(--nx-color-accent, #14b8a6) 8%, transparent));
  border-radius: var(--nx-radius-banner, 16px);
}
.nx-aurora-intro__title {
  font-family: var(--nx-font-heading, inherit);
  color: var(--nx-color-primary, #4f46e5);
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  margin: 0 0 0.5rem;
}
.nx-aurora-intro__text {
  color: var(--nx-color-text, #0f172a);
  opacity: 0.8;
  margin: 0;
}
`,
};
